/**
 * S9-SpecReady — the "part captured" confirmation after a room's questions.
 *
 * Brief acknowledgement only — the full scope is NOT shown here. It routes to
 * the next room (paint both / multi-bathroom) or to the project summary, where
 * the customer answers budget/timeline and THEN reviews the whole project's
 * scope before submitting for a free estimate. The scope list only appears at
 * that final review, so it always comes after budget/timeline.
 *
 * Never renders prices, module codes, crew-days, or margins.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import type { CustomerEstimate } from '../../lib/types';

interface LoopState {
  projectId: string;
  roomLabels: string[];
  roomIndex: number;
  serviceLine: string;
  productCode: string;
  /** Per-room service lines/product codes when rooms differ (paint both). */
  roomServiceLines?: string[];
  roomProductCodes?: string[];
}

interface SpecReadyState {
  estimate?: CustomerEstimate & { id?: string };
  /** Present when this room is part of a multi-room project loop. */
  loop?: LoopState;
}

export function SpecReady(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const specState = (location.state as SpecReadyState) ?? {};
  const estimate = specState.estimate;
  const loop = specState.loop;

  // Arrived without an estimate (e.g. direct nav) — send back to the flow.
  if (!estimate) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <h1>Let&rsquo;s finish your estimate.</h1>
          <p className="stage-card__lede">A few questions and we&rsquo;ll have your scope ready.</p>
          <div className="q-actions">
            <button type="button" className="pb-btn pb-btn--lg" onClick={() => navigate('/design/welcome')}>
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // A refused job routes to a conversation rather than a scope.
  if (estimate.status === 'REFUSED') {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <div className="progress__count">WE NEED A CLOSER LOOK</div>
          <h1>Let&rsquo;s talk this one through.</h1>
          <p className="stage-card__lede">{estimate.refusedReason}</p>
          <div className="q-actions">
            <button type="button" className="pb-btn pb-btn--lg" onClick={() => navigate('/talk')}>
              Book a call
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pending = estimate.status === 'PENDING';
  const hasMore = !!loop && loop.roomIndex + 1 < loop.roomLabels.length;

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <div className="progress__count">SCOPE CAPTURED</div>
        <h1>Got it — that&rsquo;s captured.</h1>
        <p className="stage-card__lede">
          {hasMore
            ? 'We\u2019ve got this part. Next we\u2019ll scope the rest, then you\u2019ll review the whole project.'
            : 'We\u2019ve got everything for this part. A couple of quick questions, then you\u2019ll review the whole project before we quote it.'}
        </p>

        {pending ? (
          <div className="pb-note pb-note--flag" role="note">
            One or two items in your job need a closer look. We&rsquo;ll confirm those with you
            before your estimate is final.
          </div>
        ) : null}

        {loop ? renderLoopActions() : renderSingleActions()}
      </div>
    </div>
  );

  // --- CTA variants --------------------------------------------------------

  function goToProjectSummary(): void {
    if (loop) navigate('/design/project', { state: { projectId: loop.projectId } });
  }

  function goToNextRoom(): void {
    if (!loop) return;
    navigate('/design/flow', {
      state: {
        // Top-level line kept for the same-line loop (bathrooms); per-room
        // arrays (when present) let DesignFlow pick the next room's line (paint).
        serviceLine: loop.serviceLine,
        productCode: loop.productCode,
        projectId: loop.projectId,
        roomLabels: loop.roomLabels,
        roomIndex: loop.roomIndex + 1,
        ...(loop.roomServiceLines ? { roomServiceLines: loop.roomServiceLines } : {}),
        ...(loop.roomProductCodes ? { roomProductCodes: loop.roomProductCodes } : {}),
      },
    });
  }

  function renderLoopActions(): JSX.Element {
    if (!loop) return renderSingleActions();
    const hasMore = loop.roomIndex + 1 < loop.roomLabels.length;
    const nextLabel = hasMore ? loop.roomLabels[loop.roomIndex + 1] : null;
    return (
      <div className="q-actions" style={{ flexDirection: 'column' }}>
        {hasMore ? (
          <button type="button" className="pb-btn pb-btn--lg" onClick={goToNextRoom}>
            Next: {nextLabel}
          </button>
        ) : null}
        <button
          type="button"
          className={hasMore ? 'pb-btn pb-btn--ghost pb-btn--lg' : 'pb-btn pb-btn--lg'}
          onClick={goToProjectSummary}
        >
          {hasMore ? 'That\u2019s all \u2014 continue' : 'Continue'}
        </button>
      </div>
    );
  }

  function renderSingleActions(): JSX.Element {
    // No loop context (shouldn't happen now that every flow has a project) —
    // fall back to the dashboard rather than dead-end.
    return (
      <div className="q-actions" style={{ flexDirection: 'column' }}>
        <button type="button" className="pb-btn pb-btn--lg" onClick={() => navigate('/dashboard')}>
          Back to my projects
        </button>
      </div>
    );
  }
}
